import React, { useEffect, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { CheckCircle2, ArrowUpRight, Download } from 'lucide-react';

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.07, ease: [0.4, 0, 0.2, 1] as const },
  }),
};

export function ExtensionSetupGuide({ index }: { index: number }) {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);

  useEffect(() => {
    // Check if the extension injected its root element or set a window variable
    const checkExtension = () => {
      if (document.getElementById('finn-ai-installed') || document.getElementById('finn-extension-root') || (window as any).FINN_EXTENSION_ACTIVE) {
        setIsExtensionInstalled(true);
      }
    };

    // Check immediately and then poll a few times in case it loads late
    checkExtension();
    const interval = setInterval(checkExtension, 1000);
    setTimeout(() => clearInterval(interval), 5000);

    // For demo purposes, if this is a rep who just logged in, they might not have it.
    // We'll let the DOM check handle it.
  }, []);

  return (
    <motion.div
      id="finn-tour-extension"
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderRadius: '0',
        border: '1px solid var(--admin-card-border)',
        borderLeft: isExtensionInstalled ? '4px solid var(--admin-accent-green)' : '4px solid var(--color-status-warning, #F59E0B)',
        backgroundColor: 'var(--admin-card-bg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: isExtensionInstalled ? '1px solid var(--admin-accent-green)' : '1px solid var(--color-status-warning, #F59E0B)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isExtensionInstalled ? 'var(--admin-accent-green)' : 'var(--color-status-warning, #F59E0B)'
        }}>
          {isExtensionInstalled ? <CheckCircle2 size={12} /> : <Download size={12} />}
        </div>
        <div>
          <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', margin: '0 0 2px 0' }}>
            {isExtensionInstalled ? 'Extension Installed & Ready' : 'Extension Required'}
          </h4>
          <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: 0 }}>
            {isExtensionInstalled
              ? 'Your browser is configured. Finn will automatically start on your next call.'
              : 'Please install the FinnAI Chrome extension to start recording and analyzing your calls.'}
          </p>
        </div>
      </div>
      {isExtensionInstalled ? (
        <a
          href="https://chromewebstore.google.com/detail/finn-ai-meeting-co-pilot/blfpccafpdjcepdmciippgfgeepddkei"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '12px', color: 'var(--admin-card-text)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          Manage <ArrowUpRight size={12} />
        </a>
      ) : (
        <a
          href="https://chromewebstore.google.com/detail/finn-ai-meeting-co-pilot/blfpccafpdjcepdmciippgfgeepddkei"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '12px', padding: '6px 12px', backgroundColor: 'var(--color-status-warning, #F59E0B)', color: 'var(--color-status-warning-text, #000)', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '0' }}
        >
          Install <ArrowUpRight size={12} />
        </a>
      )}
    </motion.div>
  );
}
